import React from 'react';
import {
  Users, DollarSign, FileText, TrendingUp, Calendar, Filter, Download, RefreshCw, CheckCircle, XCircle, Clock, Search, Mail, Phone, Building, CreditCard, Percent, AlertTriangle, Check, X, Edit2, Save, Wallet, Send, ArrowRight, Banknote, Bell, Trash2, Eye
} from 'lucide-react';
import { useSuperAdminContext } from '../SuperAdminContext';

export default function PayoutsTab() {
  const ctx = useSuperAdminContext();
  const { activeTab, balanceSummary, getPayoutStatusBadge, handleCreatePayout, handleRecalculateBalances, handleUpdatePayout, merchantBalances, openPayoutModal, openUpdatePayoutModal, payoutAdminNotes, payoutAmount, payoutFailureReason, payoutNotes, payoutUpdateStatus, payouts, payoutsSummary, selectedMerchant, selectedPayout, setPayoutAdminNotes, setPayoutAmount, setPayoutFailureReason, setPayoutNotes, setPayoutUpdateStatus, setShowPayoutModal, setShowUpdatePayoutModal, showPayoutModal, showUpdatePayoutModal } = ctx;

  return (
    <>
            {activeTab === 'payouts' && (
              <div className="payouts-content" data-testid="payouts-tab">
                <div className="section-header">
                  <h2><Wallet size={24} /> Merchant Payouts</h2>
                  <button className="btn-secondary" onClick={handleRecalculateBalances}>
                    <RefreshCw size={16} /> Recalculate Balances
                  </button>
                </div>

                {/* Summary Cards */}
                {balanceSummary && (
                  <div className="payout-summary-cards">
                    <div className="summary-card pending">
                      <div className="summary-icon"><DollarSign size={24} /></div>
                      <div className="summary-info">
                        <span className="summary-label">Total Pending</span>
                        <span className="summary-value">${balanceSummary.total_pending_payouts?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    <div className="summary-card processing">
                      <div className="summary-icon"><RefreshCw size={24} /></div>
                      <div className="summary-info">
                        <span className="summary-label">In Processing</span>
                        <span className="summary-value">${balanceSummary.total_processing?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    <div className="summary-card merchants">
                      <div className="summary-icon"><Users size={24} /></div>
                      <div className="summary-info">
                        <span className="summary-label">Merchants with Balance</span>
                        <span className="summary-value">{balanceSummary.merchants_with_balance || 0}</span>
                      </div>
                    </div>
                    {payoutsSummary && (
                      <div className="summary-card completed">
                        <div className="summary-icon"><CheckCircle size={24} /></div>
                        <div className="summary-info">
                          <span className="summary-label">Total Paid Out</span>
                          <span className="summary-value">${payoutsSummary.total_completed?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Merchant Balances */}
                <div className="payout-section">
                  <h3><Building size={18} /> Merchant Balances</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Business</th>
                          <th>Email</th>
                          <th>Pending Balance</th>
                          <th>Total Earned</th>
                          <th>Total Paid Out</th>
                          <th>Bank Verified</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {merchantBalances.length === 0 ? (
                          <tr>
                            <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                              No merchant balances found. Balances will appear when payments are completed.
                            </td>
                          </tr>
                        ) : (
                          merchantBalances.map((merchant) => (
                            <tr key={merchant.merchant_id} data-testid={`balance-row-${merchant.merchant_id}`}>
                              <td><strong>{merchant.business_name}</strong></td>
                              <td>{merchant.merchant_email}</td>
                              <td className="amount-cell">
                                <span className={merchant.pending_balance > 0 ? 'text-warning' : ''}>
                                  ${merchant.pending_balance?.toFixed(2) || '0.00'}
                                </span>
                              </td>
                              <td>${merchant.total_earned?.toFixed(2) || '0.00'}</td>
                              <td className="text-success">${merchant.total_paid_out?.toFixed(2) || '0.00'}</td>
                              <td>
                                {merchant.bank_verified ? (
                                  <span className="status-badge verified"><CheckCircle size={12} /> Verified</span>
                                ) : (
                                  <span className="status-badge unverified"><XCircle size={12} /> Not Verified</span>
                                )}
                              </td>
                              <td>
                                <button 
                                  className="btn-action primary"
                                  onClick={() => openPayoutModal(merchant)}
                                  disabled={!merchant.bank_verified || merchant.pending_balance <= 0}
                                  title={!merchant.bank_verified ? 'Bank not verified' : merchant.pending_balance <= 0 ? 'No balance' : 'Create payout'}
                                >
                                  <Send size={14} /> Create Payout
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payout History */}
                <div className="payout-section">
                  <h3><FileText size={18} /> Payout History</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Business</th>
                          <th>Amount</th>
                          <th>Payments</th>
                          <th>Bank</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.length === 0 ? (
                          <tr>
                            <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                              No payouts yet. Create a payout from the merchant balances above.
                            </td>
                          </tr>
                        ) : (
                          payouts.map((payout) => (
                            <tr key={payout.id} data-testid={`payout-row-${payout.id}`}>
                              <td>{new Date(payout.created_at).toLocaleDateString()}</td>
                              <td>
                                <strong>{payout.business_name}</strong>
                                <br />
                                <small className="text-muted">{payout.merchant_email}</small>
                              </td>
                              <td className="amount-cell">
                                <strong>${payout.amount?.toFixed(2)}</strong>
                              </td>
                              <td>{payout.payment_count} payments</td>
                              <td>
                                {payout.bank_account_last4 ? `****${payout.bank_account_last4}` : 'N/A'}
                              </td>
                              <td>{getPayoutStatusBadge(payout.status)}</td>
                              <td>
                                {(payout.status === 'pending' || payout.status === 'processing') && (
                                  <button 
                                    className="btn-action secondary"
                                    onClick={() => openUpdatePayoutModal(payout)}
                                  >
                                    <Edit2 size={14} /> Update
                                  </button>
                                )}
                                {payout.status === 'completed' && (
                                  <span className="text-success"><CheckCircle size={14} /> Done</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Create Payout Modal */}
                {showPayoutModal && selectedMerchant && (
                  <div className="modal-overlay">
                    <div className="modal" data-testid="create-payout-modal">
                      <h3><Send size={20} /> Create Payout</h3>
                      <div className="modal-content">
                        <div className="payout-merchant-info">
                          <p><strong>Business:</strong> {selectedMerchant.business_name}</p>
                          <p><strong>Email:</strong> {selectedMerchant.merchant_email}</p>
                          <p><strong>Available Balance:</strong> <span className="text-warning">${selectedMerchant.pending_balance?.toFixed(2)}</span></p>
                        </div>
                        <div className="form-group">
                          <label>Payout Amount *</label>
                          <div className="input-with-prefix">
                            <span className="input-prefix">$</span>
                            <input
                              type="number"
                              value={payoutAmount}
                              onChange={(e) => setPayoutAmount(e.target.value)}
                              placeholder="Enter amount"
                              max={selectedMerchant.pending_balance}
                              step="0.01"
                            />
                          </div>
                          <small className="form-help">Leave as-is to payout full balance</small>
                        </div>
                        <div className="form-group">
                          <label>Notes (Optional)</label>
                          <textarea
                            value={payoutNotes}
                            onChange={(e) => setPayoutNotes(e.target.value)}
                            placeholder="Add any notes for this payout..."
                            rows={3}
                          />
                        </div>
                        <div className="modal-actions">
                          <button className="btn-secondary" onClick={() => setShowPayoutModal(false)}>
                            Cancel
                          </button>
                          <button 
                            className="btn-primary"
                            onClick={handleCreatePayout}
                            disabled={!payoutAmount || parseFloat(payoutAmount) <= 0 || parseFloat(payoutAmount) > selectedMerchant.pending_balance}
                          >
                            <Send size={16} /> Create Payout
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Update Payout Modal */}
                {showUpdatePayoutModal && selectedPayout && (
                  <div className="modal-overlay">
                    <div className="modal" data-testid="update-payout-modal">
                      <h3><Edit2 size={20} /> Update Payout Status</h3>
                      <div className="modal-content">
                        <div className="payout-merchant-info">
                          <p><strong>Payout ID:</strong> {selectedPayout.id.slice(0, 8)}...</p>
                          <p><strong>Business:</strong> {selectedPayout.business_name}</p>
                          <p><strong>Amount:</strong> <span className="text-warning">${selectedPayout.amount?.toFixed(2)}</span></p>
                          <p><strong>Current Status:</strong> {getPayoutStatusBadge(selectedPayout.status)}</p>
                        </div>
                        <div className="form-group">
                          <label>New Status *</label>
                          <select
                            value={payoutUpdateStatus}
                            onChange={(e) => setPayoutUpdateStatus(e.target.value)}
                          >
                            <option value="">Select status...</option>
                            {selectedPayout.status === 'pending' && (
                              <>
                                <option value="processing">Processing (ACH initiated)</option>
                                <option value="completed">Completed (Funds sent)</option>
                                <option value="cancelled">Cancelled</option>
                              </>
                            )}
                            {selectedPayout.status === 'processing' && (
                              <>
                                <option value="completed">Completed (Funds delivered)</option>
                                <option value="failed">Failed</option>
                              </>
                            )}
                          </select>
                        </div>
                        {payoutUpdateStatus === 'failed' && (
                          <div className="form-group">
                            <label>Failure Reason *</label>
                            <input
                              type="text"
                              value={payoutFailureReason}
                              onChange={(e) => setPayoutFailureReason(e.target.value)}
                              placeholder="e.g., Invalid bank account, insufficient funds..."
                            />
                          </div>
                        )}
                        <div className="form-group">
                          <label>Admin Notes (Optional)</label>
                          <textarea
                            value={payoutAdminNotes}
                            onChange={(e) => setPayoutAdminNotes(e.target.value)}
                            placeholder="Add any admin notes..."
                            rows={3}
                          />
                        </div>
                        <div className="modal-actions">
                          <button className="btn-secondary" onClick={() => setShowUpdatePayoutModal(false)}>
                            Cancel
                          </button>
                          <button 
                            className="btn-primary"
                            onClick={handleUpdatePayout}
                            disabled={!payoutUpdateStatus || (payoutUpdateStatus === 'failed' && !payoutFailureReason)}
                          >
                            <Save size={16} /> Update Status
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
    </>
  );
}
